import {useEffect, useRef, useState, type FocusEvent, type MouseEvent} from 'react';

import {isNil, isNotNil} from 'ramda';

import {TOOLTIP_OPEN_DELAY} from '../../../../../../../constants/tooltipOpenDelay';
import {getTruncatedCellText} from '../utils/getTruncatedCellText';

export type TruncatedCellHoverState = {
  /** Full text of the truncated cell content. */
  label: string;
  /** Viewport rect of the revealed cell, captured on mouse enter / focus — anchors the overlay. */
  anchorRect: DOMRect;
};

/**
 * Detects hovering or keyboard-focusing a cell whose content is actually
 * truncated. Truncation is measured lazily on mouse enter / focus (scrollWidth
 * vs clientWidth on the cell's descendants) so untruncated cells never pay any
 * cost. Mouse reveal waits for the shared tooltip open delay; keyboard focus
 * reveals immediately — the intent is explicit, and only `:focus-visible`
 * focus counts (clicking a cell also focuses it via tabIndex -1 but must not
 * reveal). Disabled for wrapped cells — those already display their full
 * content. What the reveal renders (tooltip or expanded cell overlay) is
 * decided by the caller.
 */
export const useTruncatedCellHover = (isEnabled: boolean) => {
  const [hoverState, setHoverState] = useState<TruncatedCellHoverState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: timer with cleanup — clears the pending reveal timer on unmount
  useEffect(function registerRevealTimerCleanup() {
    return function clearRevealTimerOnUnmount() {
      if (isNotNil(timerRef.current)) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const measureCell = (cell: HTMLTableCellElement): TruncatedCellHoverState | null => {
    const truncatedText = isEnabled ? getTruncatedCellText(cell) : null;

    if (isNil(truncatedText)) {
      return null;
    }

    return {label: truncatedText, anchorRect: cell.getBoundingClientRect()};
  };

  const clearPendingTimer = () => {
    if (isNotNil(timerRef.current)) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleMouseEnter = (event: MouseEvent<HTMLTableCellElement>) => {
    const nextState = measureCell(event.currentTarget);

    if (isNil(nextState)) {
      return;
    }

    clearPendingTimer();
    timerRef.current = setTimeout(() => setHoverState(nextState), TOOLTIP_OPEN_DELAY);
  };

  const handleFocus = (event: FocusEvent<HTMLTableCellElement>) => {
    // Same keyboard-modality check the platform Tooltip uses: focus that arrived
    // from a mouse click (possible via tabIndex -1) does not reveal.
    if (!event.currentTarget.matches(':focus-visible')) {
      return;
    }

    const nextState = measureCell(event.currentTarget);

    if (isNil(nextState)) {
      return;
    }

    clearPendingTimer();
    setHoverState(nextState);
  };

  const hide = () => {
    clearPendingTimer();
    setHoverState(null);
  };

  return {hoverState, handleMouseEnter, handleFocus, hide};
};
